import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, RefreshCw, ShieldCheck, KeyRound, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const Captcha = ({ captcha, onRefresh, value, onChange }) => (
  <div className="space-y-2">
    <Label>Codice antibot</Label>
    <div className="flex items-center gap-3">
      {captcha ? <img src={captcha.image} alt="Codice di verifica" className="h-14 rounded-md border border-gray-200" data-testid="login-captcha-image" /> : <div className="h-14 w-[200px] bg-gray-100 rounded-md" />}
      <Button type="button" variant="outline" size="icon" onClick={onRefresh} aria-label="Nuovo codice" data-testid="login-captcha-refresh"><RefreshCw size={16} /></Button>
    </div>
    <Input required autoComplete="off" maxLength={10} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} placeholder="Digita i caratteri" data-testid="login-captcha-input" />
  </div>
);

const OtpField = ({ value, onChange }) => (
  <InputOTP maxLength={6} value={value} onChange={onChange} data-testid="login-otp-input">
    <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />)}</InputOTPGroup>
  </InputOTP>
);

const BackupCodes = ({ codes, onDone }) => (
  <div className="space-y-5" data-testid="backup-codes-panel">
    <h2 className="font-display text-2xl font-extrabold tracking-tight">Salva i codici di emergenza</h2>
    <p className="text-sm text-gray-600">Ognuno funziona una sola volta se perdi il telefono. Non verranno più mostrati.</p>
    <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md p-4" data-testid="backup-codes-list">
      {codes.map((c) => <span key={c}>{c}</span>)}
    </div>
    <div className="flex gap-3">
      <Button variant="outline" onClick={() => { navigator.clipboard.writeText(codes.join("\n")); toast.success("Codici copiati"); }} data-testid="backup-codes-copy"><Copy size={16} /> Copia</Button>
      <Button className="bg-emerald-900 hover:bg-emerald-800" onClick={onDone} data-testid="backup-codes-continue">Ho salvato i codici, continua</Button>
    </div>
  </div>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("creds");
  const [f, setF] = useState({ email: "", password: "", captcha_answer: "", website: "" });
  const [captcha, setCaptcha] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);
  const [codes, setCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadCaptcha = () => api.get("/auth/captcha").then((r) => setCaptcha(r.data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { loadCaptcha(); }, []);

  if (user && !codes) return <Navigate to="/admin" replace />;

  const login = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { ...f, captcha_id: captcha?.captcha_id });
      setMfa(data); setStep(data.step);
    } catch (err) {
      setError(errMsg(err)); setF((x) => ({ ...x, captcha_answer: "" })); loadCaptcha();
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/2fa/verify", { temp_token: mfa.temp_token, code });
      if (data.backup_codes) { setCodes(data.backup_codes); setStep("backup"); }
      setUser(data.user);
      if (!data.backup_codes) nav("/admin");
    } catch (err) {
      setError(errMsg(err)); setCode("");
      if (err.response?.status === 401 && /Sessione|Token/.test(errMsg(err))) setStep("creds");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-emerald-900 text-white p-14 relative overflow-hidden">
        <img src="/img/rocard-logo.png" alt="Rocard" className="h-12 w-auto self-start" />
        <div>
          <ShieldCheck size={44} className="text-emerald-200" />
          <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight leading-[1.05]">Pannello recensioni<br />e Garanzia</h1>
          <p className="mt-4 text-emerald-100 max-w-md">Accesso protetto da password, codice antibot e verifica in due passaggi.</p>
        </div>
        <p className="text-xs text-emerald-200/70">Area riservata. Ogni accesso viene registrato.</p>
        <div className="absolute -right-24 -bottom-24 h-96 w-96 rounded-full border-[40px] border-white/5" aria-hidden="true" />
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg p-8 fade-up">
          {error && <p className="mb-5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}
          {step === "creds" && (
            <form onSubmit={login} className="space-y-5" data-testid="login-form">
              <div><h2 className="font-display text-3xl font-extrabold tracking-tight">Accedi</h2><p className="text-sm text-gray-600 mt-1">Passo 1 di 2 · Credenziali</p></div>
              <div className="space-y-2"><Label>Email</Label><Input required type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></div>
              <div className="space-y-2"><Label>Password</Label><Input required type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></div>
              <Captcha captcha={captcha} onRefresh={loadCaptcha} value={f.captcha_answer} onChange={(v) => setF({ ...f, captcha_answer: v })} />
              <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} />
              <Button type="submit" disabled={loading} className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="login-submit-button">
                {loading && <Loader2 className="animate-spin mr-2" size={16} />} Continua
              </Button>
            </form>
          )}
          {(step === "totp" || step === "totp_setup") && (
            <form onSubmit={verify} className="space-y-5" data-testid="login-2fa-form">
              <div><h2 className="font-display text-3xl font-extrabold tracking-tight">Verifica in due passaggi</h2><p className="text-sm text-gray-600 mt-1">Passo 2 di 2</p></div>
              {step === "totp_setup" && (
                <div className="space-y-3" data-testid="totp-setup-panel">
                  <p className="text-sm text-gray-700">Primo accesso: scansiona il QR con Google Authenticator, Microsoft Authenticator o 1Password.</p>
                  <img src={mfa.qr} alt="QR code 2FA" className="h-44 w-44 border border-gray-200 rounded-md" data-testid="totp-qr" />
                  <p className="text-xs text-gray-500">Oppure inserisci la chiave: <span className="font-mono text-gray-800 break-all" data-testid="totp-secret">{mfa.secret}</span></p>
                </div>
              )}
              {useBackup ? (
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Codice di emergenza" data-testid="login-backup-input" />
              ) : <OtpField value={code} onChange={setCode} />}
              {step === "totp" && (
                <button type="button" className="text-sm text-emerald-900 font-semibold inline-flex items-center gap-1 hover:underline" onClick={() => { setUseBackup(!useBackup); setCode(""); }} data-testid="login-toggle-backup">
                  <KeyRound size={14} /> {useBackup ? "Usa l'app di autenticazione" : "Usa un codice di emergenza"}
                </button>
              )}
              <Button type="submit" disabled={loading || code.length < 6} className="w-full h-11 bg-emerald-900 hover:bg-emerald-800" data-testid="login-verify-button">
                {loading && <Loader2 className="animate-spin mr-2" size={16} />} Verifica e accedi
              </Button>
            </form>
          )}
          {step === "backup" && codes && <BackupCodes codes={codes} onDone={() => { setCodes(null); nav("/admin"); }} />}
        </div>
      </div>
    </div>
  );
}
